/**
 * turf_color.ts — THE ONE COLOUR FUNCTION the grass layer and the ground's turf share
 * (2026-09-25, visual-realism R2).
 *
 * A blade field whose colour does not match the ground it stands on FLOATS: the eye reads a green
 * carpet laid over a brown floor. So the colour a meadow reads at distance (the ground's turf
 * layer) and the colour the blades carry (lib/grass) come from ONE rule over ONE set of inputs:
 *
 *   turf = the biome palette looked up from the ground's TINT, then pulled toward STRAW by dryness and
 *   toward a deep, saturated green by wetness.
 *
 * Inputs: the ground's own per-vertex climate channel — `gTint.rgb` (the biome-weighted BIOME_TINT of
 * lib/ground_layers.ts), `gTint.w` (dryness) and `gMask.z` (wetness) — because the v2 ground carries no
 * biome weights (turf_core.ts says why the lookup is Shepard over the six tints). Output: LINEAR RGB.
 *
 * `turfColorAt` (turf_core.ts, pure) is the CPU rule: the grass field bakes it per 2 m from the SAME
 * attributes read off the resident chunk mesh, and the far tier's macro bake (`ground_rules.ruleColour`)
 * calls it for the turf layer. `turfColorAtNode` below is its TSL mirror: the ground shader's turf layer
 * (full and minimal paths) calls it per pixel. Same tables, same formula — change both or neither.
 *
 * Cosmos identity rides the TINT, not a baked alien texture (the research's R1 doctrine): the plains
 * are an olive-straw grass, the crystal forest a cool teal, the wetland a deep green-teal, the fungal
 * basin a mauve moss-grass — every one a believable plant colour, pushed only a little off Earth.
 */
import { vec3, float, mix, clamp, dot } from 'three/tsl';
import type { Biome } from '@shared/terrain.js';
import {
  TURF_ORDER, TURF_LINEAR, TURF_DRY_LINEAR, TURF_WET_LINEAR, TURF_TINTS, TURF_DRY_MAX, TURF_WET_MAX, TURF_SHEPARD_EPS,
} from './turf_core.js';
// The palette, the pulls and the CPU rule live in the pure core (the ground's workers read it too).
export {
  TURF_ORDER, TURF_SRGB, TURF_DRY_SRGB, TURF_WET_SRGB, TURF_DRY_MAX, TURF_WET_MAX, TURF_SHEPARD_EPS,
  TURF_LINEAR, TURF_DRY_LINEAR, TURF_WET_LINEAR, TURF_TINTS,
  srgbByteToLinear, linearToSrgbByte, turfTintOf, turfColorAt, turfColor,
} from './turf_core.js';

/**
 * Grass layer density per biome (0..1 of the layer's lattice): the wetland and the plains carry a
 * meadow, the desert a few tufts. The layer multiplies it by the canopy, slope, water, road and
 * snow rules (lib/grass/grass_rules.ts).
 */
export const GRASS_DENSITY: Record<Biome, number> = {
  regolith_plains: 0.92, crystal_forest: 0.62, dune_desert: 0.06, rocky_highlands: 0.55, moss_wetland: 1.0, fungal_basin: 0.4,
};
/** Mean blade height per biome (m): tall wet meadow, short alpine turf. */
export const GRASS_HEIGHT_M: Record<Biome, number> = {
  regolith_plains: 0.55, crystal_forest: 0.46, dune_desert: 0.38, rocky_highlands: 0.3, moss_wetland: 0.78, fungal_basin: 0.34,
};

const ORDER: readonly Biome[] = TURF_ORDER;

/** Weighted mean of a per-biome table (weights normalised; all-zero → the first biome's value). */
export function perBiome(table: Record<Biome, number>, w: ArrayLike<number>): number {
  let s = 0, v = 0;
  for (let i = 0; i < 6; i++) { const k = Math.max(0, w[i] ?? 0); s += k; v += table[ORDER[i]] * k; }
  return s <= 1e-6 ? table[ORDER[0]] : v / s;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
/**
 * TSL mirror of `turfColorAt` — THE ground's call (lib/wetness.ts `applyGroundV2`, the turf layer) with its
 * vertex attributes: `tint` = gTint.xyz, `dry` = gTint.w, `wet` = gMask.z. Returns LINEAR RGB. Same tables,
 * same Shepard weights, same pulls as the CPU rule in turf_core.ts.
 */
export function turfColorAtNode(tint: N, dry: N, wet: N): N {
  let ws: N = float(0), acc: N = vec3(0, 0, 0);
  for (let i = 0; i < 6; i++) {
    const t = TURF_TINTS[i], p = TURF_LINEAR[i];
    const dv: N = tint.sub(vec3(t[0], t[1], t[2]));
    const k: N = float(1).div(dot(dv, dv).add(TURF_SHEPARD_EPS));
    ws = ws.add(k); acc = acc.add(vec3(p[0], p[1], p[2]).mul(k));
  }
  const base: N = acc.div(ws);
  const d: N = clamp(float(dry), 0, 1).mul(TURF_DRY_MAX), q: N = clamp(float(wet), 0, 1).mul(TURF_WET_MAX);
  const dried: N = mix(base, vec3(TURF_DRY_LINEAR[0], TURF_DRY_LINEAR[1], TURF_DRY_LINEAR[2]), d);
  return mix(dried, vec3(TURF_WET_LINEAR[0], TURF_WET_LINEAR[1], TURF_WET_LINEAR[2]), q);
}

/**
 * The same from six biome weights packed as the v1 chunk attributes (`wA` = w0..w2, `wB` = w3..w5): the
 * weights' tint (lib/ground_rules.ts's gTint formula), then `turfColorAtNode`.
 */
export function turfColorNode(wA: N, wB: N, dry: N, wet: N): N {
  const tv = (i: number): N => vec3(TURF_TINTS[i][0], TURF_TINTS[i][1], TURF_TINTS[i][2]);
  const s: N = dot(wA, vec3(1, 1, 1)).add(dot(wB, vec3(1, 1, 1))).max(1e-6);
  const tint: N = tv(0).mul(wA.x).add(tv(1).mul(wA.y)).add(tv(2).mul(wA.z))
    .add(tv(3).mul(wB.x)).add(tv(4).mul(wB.y)).add(tv(5).mul(wB.z)).div(s);
  return turfColorAtNode(tint, dry, wet);
}
